import React, { useEffect, useState } from 'react'
import { Icon, message, Modal, Spin } from 'antd'

import beginningImage from '../../img/beginning_of_lesson.svg'
import endImage from '../../img/end_of_lesson.svg'

import './styles.scss'

import ScenarioCreationForm from './ScenarioCreationForm'

import {
  saveScenarioData,
  createScenarioData,
  getScenarioData
} from '../../../../../../../../../services/api/courses'

import actions from '../../../../../../../../../store/actions'
import { useDispatch } from 'react-redux'

import nanoid from 'nanoid'
import _ from 'lodash'

const scenarioOptions = {
  beginning: {
    image: beginningImage,
    description: 'at the beginning'
  },
  end: {
    image: endImage,
    description: 'end of lesson'
  }
}

function ScenarioCreation({
  onCancel,
  scenarioId,
  setScenarioId,
  lessonId,
  courseId,
  pageStructureData,
  pages,
  lessonUpdate,
  scenarioData: scenarioDataFromProps,
  backgroundColor,
  backgroundImageLink,
  backgroundImageId,
  backgroundImageOpacity
}) {
  const [selectedOption, setSelectedOption] = useState('beginning')
  const [isSaving, setIsSaving] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [scenarioData, setScenarioData] = useState({})

  const dispatch = useDispatch()

  const { handleLessonInput } = actions.lessonBuilder

  useEffect(() => {
    if (scenarioId) {
      fetchScenarioById(scenarioId)
    } else if (!_.isEmpty(scenarioDataFromProps)) {
      handleInitialScenarioData()
    }
  }, [])

  const handleInitialScenarioData = () => {
    const {
      scenario,
      options,
      correct_answer,
      feedback
    } = scenarioDataFromProps

    const newOptions = parseOptions(options, correct_answer)

    const data = {
      answers: newOptions,
      feedback
    }

    setScenarioData({ ...data, story: scenario })
    handleSubmit({ ...data, scenarioStory: scenario })
  }

  function parseOptions(optionsArray, correctAnswer) {
    const resultArray = []

    optionsArray.forEach((option, index) => {
      const isCorrect = String(correctAnswer) === String(index + 1)
      resultArray.push({
        index: nanoid(),
        answer: option,
        correct: isCorrect,
        rating: isCorrect ? 5 : 1
      })
    })

    return resultArray
  }

  const fetchScenarioById = id => {
    setIsFetching(true)

    getScenarioData(id)
      .then(res => {
        if (res && res.data) {
          const { position, ...data } = res.data
          setScenarioData(data)
          setSelectedOption(position)
        }
      })
      .catch(err => {
        console.log(err)
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const handleSelection = key => {
    setSelectedOption(key)
  }

  const handleSubmit = async (data, type) => {
    const {
      answers,
      scenarioName = '',
      scenarioStory,
      feedback,
      attemptsCount = 0,
      scenarioMediaId = null,
      audioId = null
    } = data

    const payload = {
      answers,
      name: scenarioName,
      story: scenarioStory,
      feedback,
      attempts_count: attemptsCount,
      media_id: scenarioMediaId,
      position: selectedOption,
      story_media_id: audioId,
      lesson_id: lessonId,
      course_id: courseId
    }

    setIsSaving(true)

    let newScenarioId = null

    try {
      let response = null
      if (scenarioId) {
        response = await saveScenarioData(scenarioId, payload)
      } else {
        response = await createScenarioData(lessonId, payload)
      }

      if (response && response.data) {
        newScenarioId = response.data.id
        setScenarioId(newScenarioId)
        dispatch(handleLessonInput('scenarioId', newScenarioId))
      } else {
        message.error('Something went wrong')
      }
    } catch (err) {
      console.log(err)
      message.error('Something went wrong')
    } finally {
      setIsSaving(false)

      if (newScenarioId) {
        if (type === 'insert') {
          handleInsertScenario(newScenarioId)
        } else {
          removeScenario(newScenarioId)
        }
      }
    }
  }

  const removeScenario = id => {
    const scenarioPageIds = pageStructureData
      .filter(page => page.data.some(el => el.type === 'SCENARIO'))
      .map(page => page.id)

    const newData = pageStructureData.filter(
      page => !scenarioPageIds.includes(page.id)
    )
    const newPages = pages.filter(page => !scenarioPageIds.includes(page.id))

    if (newData.length === pageStructureData.length) return

    dispatch(handleLessonInput('currentPage', 1))
    dispatch(handleLessonInput('pages_landscape', newData))
    dispatch(handleLessonInput('pages', newPages))
    lessonUpdate({ pages_landScape: newData, pages: newPages })
  }

  const handleInsertScenario = async id => {
    const backgroundItem = backgroundImageId
      ? {
          uid: nanoid(8),
          type: 'BACKGROUND_IMAGE',
          params: {
            content: backgroundImageLink,
            content_id: backgroundImageId,
            opacity: backgroundImageOpacity
          }
        }
      : {
          uid: nanoid(8),
          type: 'BACKGROUND_COLOR',
          display: 'BACKGROUND_COLOR',
          params: {
            content: backgroundColor,
            content_id: null,
            opacity: 1
          }
        }
    const newPageData = {
      uid: nanoid(8),
      size: { width: 1024, height: 768 },
      orientation: 'landscape',
      data: [
        {
          uid: nanoid(8),
          type: 'SCENARIO',
          display: 'CONTENT',
          params: {
            content: id,
            x: 0,
            y: 0,
            width: 1023,
            height: 552
          }
        },
        backgroundItem
      ]
    }

    const newPage = {
      data: [],
      orientation: 'portrait',
      size: { width: 1024, height: 768 }
    }

    const newData = pageStructureData.filter(
      page => !page.data.some(element => element.type === 'SCENARIO')
    )
    const newPages = pages.filter(
      (_, index) =>
        !pageStructureData[index]?.data.some(
          element => element.type === 'SCENARIO'
        )
    )

    switch (selectedOption) {
      case 'beginning':
        newData.unshift(newPageData)
        newPages.unshift(newPage)
        break

      case 'end':
        newData.push(newPageData)
        newPages.push(newPage)
        break

      default:
        break
    }

    newData.forEach((page, index) => {
      page.ordering = index + 1
    })

    dispatch(handleLessonInput('pages_landscape', newData))
    dispatch(handleLessonInput('pages', newPages))
    lessonUpdate({ pages_landScape: newData, pages: newPages })
    message.success(`Scenario added at the ${selectedOption} of the lesson.`)
    onCancel()
  }

  console.log({ scenarioData })

  return (
    <Modal
      className="scenario-creation-modal"
      visible
      footer={null}
      closable={false}
      centered
      width="100%"
      style={{ maxWidth: '90vw' }}
    >
      <Spin spinning={isFetching}>
        <div className="scenario-creation">
          <div className="scenario-creation__header">
            <h3>Create Scenario AI</h3>
            <Icon type="close" onClick={onCancel} />
          </div>

          <div className="scenario-creation__body">
            <div className="scenario-creation__options">
              {Object.entries(scenarioOptions).map(([key, values], index) => (
                <div
                  key={key + index}
                  className={`scenario-creation__option ${
                    key === selectedOption ? 'active' : 'none-active'
                  }`}
                  onClick={() => handleSelection(key)}
                >
                  <div className="scenario-creation__option-item">
                    <img src={values.image} alt="" />
                    <span>{values.description}</span>
                  </div>
                </div>
              ))}
            </div>

            <ScenarioCreationForm
              onSubmit={handleSubmit}
              isSaving={isSaving}
              isFetching={isFetching}
              scenarioData={scenarioData}
            />
          </div>
        </div>
      </Spin>
    </Modal>
  )
}

export default ScenarioCreation
